import { useEffect, useRef, useState } from 'react';
import { api, type AssistantReply, type MeResponse } from '../api';
import { messageFor } from '../App';
import { toWav16kMono } from '../audio';
import { openWorkingMicrophone } from '../mic';
import { createEndpointDetector, type ListenState } from '../vad';

/**
 * The type-or-speak input, shared by the calendar and the Assistant tab.
 *
 * It lives in both places because the moment you want to record something is the moment you
 * are looking at the week, not the moment you decide to visit a separate screen. Same
 * component either way, so the two cannot drift in what they accept or how they behave.
 */
const INPUT_PREFERENCE_KEY = 'unschool.audio_input';

export function AssistantBar({
  me,
  onDone,
  compact = false,
  conversationId,
  placeholder,
  sendLabel,
  autoFocus = false,
}: {
  me: MeResponse;
  onDone: (reply: AssistantReply, said: string) => void;
  compact?: boolean;
  /** Continues an open thread instead of starting a new one. */
  conversationId?: string;
  placeholder?: string;
  sendLabel?: string;
  autoFocus?: boolean;
}) {
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [recording, setRecording] = useState(false);

  const recorder = useRef<MediaRecorder>();
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef(0);

  // A live level meter, because "did it hear me" is the question people actually have while
  // recording, and answering it after the fact — with a transcript that says [inaudible] —
  // is far too late. A flat bar while you speak means the wrong input is selected.
  const [level, setLevel] = useState(0);
  const [listenState, setListenState] = useState<ListenState>('waiting');
  const [confirmed, setConfirmed] = useState<string>();
  const [device, setDevice] = useState<string>('');
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([]);
  const [chosenInput, setChosenInput] = useState<string>(
    () => localStorage.getItem(INPUT_PREFERENCE_KEY) ?? 'default',
  );
  const meter = useRef<{ context: AudioContext; frame: number } | null>(null);

  async function startMeter(stream: MediaStream, onEnded: () => void) {
    const context = new AudioContext();
    // Chrome's autoplay policy starts an AudioContext suspended, and we are past the user
    // gesture by the time getUserMedia resolves. A suspended context feeds the analyser
    // nothing, so the meter reads zero and accuses a perfectly good microphone.
    if (context.state === 'suspended') await context.resume();

    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    context.createMediaStreamSource(stream).connect(analyser);

    const samples = new Float32Array(analyser.fftSize);
    // Endpointing runs off the same levels as the meter, so it costs nothing extra.
    const detector = createEndpointDetector(Date.now());
    let finished = false;

    const tick = () => {
      analyser.getFloatTimeDomainData(samples);
      let sumSquares = 0;
      for (const sample of samples) sumSquares += sample * sample;
      const rms = Math.sqrt(sumSquares / samples.length);
      // Speech sits well below 1.0, so scale for visibility rather than showing a sliver.
      setLevel(Math.min(1, rms * 8));

      const state = detector.push(rms, Date.now());
      setListenState(state);
      if (state === 'ended' && !finished) {
        finished = true;
        onEnded();
        return;
      }
      meter.current = { context, frame: requestAnimationFrame(tick) };
    };
    meter.current = { context, frame: requestAnimationFrame(tick) };
  }

  function stopMeter() {
    if (!meter.current) return;
    cancelAnimationFrame(meter.current.frame);
    void meter.current.context.close();
    meter.current = null;
    setLevel(0);
  }

  const capped = (me.usage?.remainingCents ?? 1) <= 0;

  async function send() {
    const said = message.trim();
    if (!said) return;
    setBusy(true);
    setError(undefined);
    try {
      const reply = await api.chat(said, conversationId);
      setMessage('');
      // Something a parent stated needs no approval step, so say plainly that it landed
      // rather than leaving them wondering whether it did.
      if (reply.mutations.length > 0) {
        setConfirmed(reply.reply);
        window.setTimeout(() => setConfirmed(undefined), 6000);
      }
      onDone(reply, said);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function startRecording() {
    setError(undefined);
    try {
      // Picks a device that is actually producing something, rather than trusting the
      // browser's choice. See mic.ts for why that cannot be trusted.
      const chosen = await openWorkingMicrophone(
        chosenInput === 'default' ? undefined : chosenInput,
      );
      const stream = chosen.stream;

      console.info(`[mic] using “${chosen.label}” (${chosen.via})`);
      chunks.current = [];
      startedAt.current = Date.now();

      // Refresh the list now that permission is granted; labels are hidden before that.
      void navigator.mediaDevices
        .enumerateDevices()
        .then((all) => setInputs(all.filter((d) => d.kind === 'audioinput' && d.deviceId)))
        .catch(() => undefined);

      // Surface which input is actually in use. "Hearing nothing" is ambiguous until you
      // know whether the browser picked the headset, the webcam, or a virtual device.
      const track = stream.getAudioTracks()[0];
      setDevice(chosen.label || track?.label || 'unknown input');
      if (track && track.muted) {
        setError(
          `The selected microphone (${track.label || 'unknown'}) is muted at the system ` +
            `level. Unmute it, or pick a different input in Chrome's site settings.`,
        );
      }

      const media = new MediaRecorder(stream);
      // Stops itself once you stop talking, so recording is press-once rather than
      // press-talk-press. The Stop button stays for anyone who would rather not wait.
      await startMeter(stream, () => stopRecording());
      media.ondataavailable = (e) => chunks.current.push(e.data);
      media.onstop = async () => {
        stopMeter();
        // Release the microphone immediately; leaving it open keeps the browser's recording
        // indicator lit, which is alarming and rightly so.
        stream.getTracks().forEach((track) => track.stop());

        const wallClockSeconds = (Date.now() - startedAt.current) / 1000;
        if (wallClockSeconds < 0.6) {
          setError('That was too short to hear. Hold the button while you speak.');
          return;
        }

        setBusy(true);
        try {
          // Speech APIs do not accept the WebM the browser prefers, so convert before
          // upload. The decoded duration is used for metering rather than wall clock,
          // since it is what the provider actually bills for.
          const audio = await toWav16kMono(new Blob(chunks.current, { type: media.mimeType }));
          const reply = await api.voice(audio.blob, audio.durationSec, conversationId);
          if (reply.mutations.length > 0) {
            setConfirmed(reply.reply);
            window.setTimeout(() => setConfirmed(undefined), 6000);
          }
          onDone(reply, '(spoken)');
        } catch (err) {
          setError(messageFor(err));
        } finally {
          setBusy(false);
        }
      };

      media.start();
      recorder.current = media;
      setRecording(true);
    } catch (err) {
      setError(
        `Could not use the microphone: ${messageFor(err)}. Check that this site is allowed ` +
          `to record audio.`,
      );
    }
  }

  function stopRecording() {
    recorder.current?.stop();
    recorder.current = undefined;
    setRecording(false);
    setListenState('waiting');
  }

  useEffect(() => stopMeter, []);

  return (
    <div>
      {capped && (
        <div className="notice warn">
          This month's AI budget is spent, so the assistant is paused until next month. You can
          still add and edit entries by hand on the Log tab.
        </div>
      )}
      {error && <div className="notice error">{error}</div>}
      {confirmed && <div className="notice ok">✓ {confirmed}</div>}

      <div className="field" style={{ marginBottom: 10 }}>
        <textarea
          rows={compact ? 2 : 3}
          value={message}
          disabled={busy || capped}
          autoFocus={autoFocus}
          placeholder={
            placeholder ??
            (compact
              ? 'We did math at the kitchen table from 9 to 9:45, working on fractions.'
              : 'We spent an hour from 2 to 3 talking about vegetables in the garden.')
          }
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send();
          }}
        />
      </div>

      <div className="mic">
        <button className="primary" disabled={busy || capped || !message.trim()} onClick={() => void send()}>
          {busy ? 'Working…' : (sendLabel ?? 'Record it')}
        </button>
        <button
          className={recording ? 'recording' : ''}
          disabled={busy || capped}
          onClick={() => (recording ? stopRecording() : void startRecording())}
        >
          {recording ? 'Stop' : 'Speak instead'}
        </button>
        {inputs.length > 1 && !recording && (
          <select
            className="mic-picker"
            value={chosenInput}
            title="Which microphone to record from"
            onChange={(e) => {
              setChosenInput(e.target.value);
              localStorage.setItem(INPUT_PREFERENCE_KEY, e.target.value);
            }}
          >
            {inputs.map((input) => (
              <option key={input.deviceId} value={input.deviceId}>
                {input.label || 'Microphone'}
              </option>
            ))}
          </select>
        )}
        {recording ? (
          <span className="mic-level" title="Input level">
            <span style={{ width: `${Math.round(level * 100)}%` }} />
          </span>
        ) : null}
        <span className="muted">
          {recording
            ? listenState === 'speaking'
              ? 'Listening — stops on its own when you pause.'
              : level < 0.02
                ? `Hearing nothing from “${device}”. Pick a different input in Chrome's site settings.`
                : `Listening to “${device}” — go ahead.`
            : busy
              ? 'Working…'
              : 'Cmd/Ctrl + Enter to send'}
        </span>
      </div>
    </div>
  );
}
